/**
 * Module dependencies.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Badge } from 'app/components/ui/badge';
import { cn } from 'app/lib/utils';
import { ListOrdered } from 'lucide-react';
import { CryptoBotPosition, useCryptoBotPositions } from 'app/hooks/requests/crypto-bot/use-crypto-bot';

/**
 * Compact number formatting.
 */

function num(value: number | null | undefined, dp = 2): string {
  return value === null || value === undefined
    ? '—'
    : value.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function qtyFmt(value: number): string {
  return String(parseFloat(value.toFixed(8)));
}

/**
 * Signed value with colour.
 */

function Pnl({ value, pct }: { value: number | null | undefined; pct?: number | null }): JSX.Element {
  if (value === null || value === undefined) {
    return <span className={'text-muted-foreground'}>{'—'}</span>;
  }

  const cls = value > 0 ? 'text-emerald-600' : value < 0 ? 'text-destructive' : 'text-muted-foreground';

  return (
    <span className={cn('tabular-nums', cls)}>
      {value > 0 ? '+' : ''}
      {num(value)}
      {pct !== null && pct !== undefined ? (
        <span className={'ml-1 text-xs'}>
          {'('}
          {pct.toFixed(1)}
          {'%)'}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Export `CryptoBotPositions` component.
 */

export function CryptoBotPositions(): JSX.Element {
  const [translate] = useTranslation();
  const { data, isLoading } = useCryptoBotPositions();

  if (isLoading || !data) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  const empty = data.open.length === 0 && data.closed.length === 0;

  return (
    <div className={'w-full space-y-6'}>
      <Title>{translate('cryptoBot.positions.title')}</Title>

      {empty ? (
        <div
          className={
            'flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-20 text-center'
          }
        >
          <ListOrdered className={'h-8 w-8 text-muted-foreground'} />

          <div className={'text-base font-semibold text-foreground'}>{translate('cryptoBot.positions.emptyTitle')}</div>

          <p className={'max-w-md text-sm text-muted-foreground'}>
            {translate('cryptoBot.positions.emptyDescription')}
          </p>
        </div>
      ) : null}

      {/* Open */}
      {data.open.length > 0 && (
        <section className={'space-y-2'}>
          <h2 className={'text-sm font-semibold uppercase tracking-wide text-muted-foreground'}>
            {translate('cryptoBot.positions.open')}
          </h2>

          <div className={'overflow-x-auto rounded-xl border border-border bg-card'}>
            <table className={'w-full border-collapse text-sm'}>
              <thead>
                <tr
                  className={
                    'border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground'
                  }
                >
                  <th className={'px-3 py-2 font-semibold'}>{translate('cryptoBot.positions.pair')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>{translate('cryptoBot.positions.qty')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>{translate('cryptoBot.positions.entry')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>{translate('cryptoBot.positions.current')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>{translate('cryptoBot.positions.stop')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>
                    {translate('cryptoBot.positions.takeProfit')}
                  </th>
                  <th className={'px-3 py-2 text-right font-semibold'}>
                    {translate('cryptoBot.positions.unrealized')}
                  </th>
                </tr>
              </thead>

              <tbody className={'divide-y divide-border'}>
                {data.open.map((position: CryptoBotPosition) => (
                  <tr className={'hover:bg-muted/40'} key={position.id}>
                    <td className={'px-3 py-2'}>
                      <span className={'font-medium text-foreground'}>{position.symbol}</span>
                      <span className={'ml-2 text-xs text-muted-foreground'}>{position.strategy}</span>
                    </td>
                    <td className={'px-3 py-2 text-right tabular-nums'}>{qtyFmt(position.qty)}</td>
                    <td className={'px-3 py-2 text-right tabular-nums'}>{num(position.entryPrice)}</td>
                    <td className={'px-3 py-2 text-right tabular-nums'}>{num(position.currentPrice)}</td>
                    <td className={'px-3 py-2 text-right tabular-nums text-muted-foreground'}>
                      {num(position.stopPrice)}
                    </td>
                    <td className={'px-3 py-2 text-right tabular-nums text-muted-foreground'}>
                      {num(position.takeProfitPrice)}
                    </td>
                    <td className={'px-3 py-2 text-right'}>
                      <Pnl pct={position.unrealizedPct} value={position.unrealizedPnl} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Closed */}
      {data.closed.length > 0 && (
        <section className={'space-y-2'}>
          <h2 className={'text-sm font-semibold uppercase tracking-wide text-muted-foreground'}>
            {translate('cryptoBot.positions.closed')}
          </h2>

          <div className={'overflow-x-auto rounded-xl border border-border bg-card'}>
            <table className={'w-full border-collapse text-sm'}>
              <thead>
                <tr
                  className={
                    'border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground'
                  }
                >
                  <th className={'px-3 py-2 font-semibold'}>{translate('cryptoBot.positions.pair')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>{translate('cryptoBot.positions.qty')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>{translate('cryptoBot.positions.entry')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>{translate('cryptoBot.positions.exit')}</th>
                  <th className={'px-3 py-2 text-right font-semibold'}>{translate('cryptoBot.positions.pnl')}</th>
                  <th className={'px-3 py-2 font-semibold'}>{translate('cryptoBot.positions.reason')}</th>
                </tr>
              </thead>

              <tbody className={'divide-y divide-border'}>
                {data.closed.map((position: CryptoBotPosition) => (
                  <tr className={'hover:bg-muted/40'} key={position.id}>
                    <td className={'px-3 py-2'}>
                      <span className={'font-medium text-foreground'}>{position.symbol}</span>
                      <span className={'ml-2 text-xs text-muted-foreground'}>{position.strategy}</span>
                    </td>
                    <td className={'px-3 py-2 text-right tabular-nums'}>{qtyFmt(position.qty)}</td>
                    <td className={'px-3 py-2 text-right tabular-nums'}>{num(position.entryPrice)}</td>
                    <td className={'px-3 py-2 text-right tabular-nums'}>{num(position.exitPrice)}</td>
                    <td className={'px-3 py-2 text-right'}>
                      <Pnl value={position.realizedPnl} />
                    </td>
                    <td className={'px-3 py-2'}>
                      <Badge variant={'outline'}>{position.closeReason ?? '—'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
